import { Button, Card, Divider, Grid, Group, Image, NumberInput, Select, Switch, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import AssetPickerModal from '../Assets/AssetPickerModal';
import URLFilterConfig from '../UrlFilterConfig';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import SuperAdminArea from '../SuperAdmin/SuperAdminArea';
import InitialMessagesEditor from './InitialMessagesEditor';
import WidgetColorsSection from './WidgetColorsSection';
import { HELP, applyExample } from './superAdmin/superAdminHelp';

const isLikelyImageUrl = (value) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

// Launcher shape per device.
const LAUNCHER_FORMAT_OPTIONS = [
  { value: 'auto', label: 'Auto (line, bubble on scroll)' },
  { value: 'line', label: 'Line bar' },
  { value: 'bubble', label: 'Bubble' },
];

const CustomizeTab = ({ chatBot, handleChange, domainLocale, isSuperAdmin = false, brandContext, brandHref }) => {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const bubbleIconValid = isLikelyImageUrl(chatBot.bubbleIcon);

  // All launcher/notification prefs live in one nested `appearance` object.
  // `appearance` is the render-time snapshot used for reading current values;
  // writes dispatch just the patch and handleChange merges it functionally
  // onto the latest state, so back-to-back updates don't clobber each other.
  const appearance = chatBot.appearance || {};
  const setAppearance = (patch) => handleChange({ target: { name: 'appearance', value: patch } });

  return (
    <Card withBorder mt="sm">
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Bot Title</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <InitialMessagesEditor
            value={chatBot.title || { default: '' }}
            onChange={(value) => handleChange({ target: { name: 'title', value } })}
            domainLocale={domainLocale}
            singleLine
            idPrefix="title"
            placeholder="e.g. Sales Assistant"
          />
          <Text size="xs" c="dimmed" mt={4}>
            Name shown in the chat window header for each language
          </Text>
        </Grid.Col>
      </Grid>

      {/* "Invite Message" in the UI is persisted as `chatBot.inputPlaceholder`. */}
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Invite Message</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <InitialMessagesEditor
            value={chatBot.inputPlaceholder || { default: '' }}
            onChange={(value) => handleChange({ target: { name: 'inputPlaceholder', value } })}
            domainLocale={domainLocale}
            singleLine
            idPrefix="inputPlaceholder"
            placeholder="e.g. Ask me anything..."
          />
          <Text size="xs" c="dimmed" mt={4}>
            Shown on the launcher line, in the notification bar, and as the chat input placeholder
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Bubble Icon</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Group align="flex-start" wrap="nowrap" gap="sm">
            {chatBot.bubbleIcon && bubbleIconValid && (
              <Image
                src={chatBot.bubbleIcon}
                w={36}
                h={36}
                radius="sm"
                fit="contain"
                alt="Bubble icon preview"
                fallbackSrc="data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3C/svg%3E"
              />
            )}
            <TextInput
              style={{ flex: 1 }}
              name="bubbleIcon"
              placeholder="e.g. https://yourshop.com/logo.png"
              value={chatBot.bubbleIcon || ''}
              error={!bubbleIconValid ? 'Enter a valid http(s) image URL' : undefined}
              onChange={(e) => handleChange({ target: { name: 'bubbleIcon', value: e.target.value || null } })}
              rightSection={
                <Button variant="default" size="xs" onClick={() => setIconPickerOpen(true)}>
                  Browse
                </Button>
              }
              rightSectionWidth={80}
            />
          </Group>
          <AssetPickerModal
            show={iconPickerOpen}
            close={() => setIconPickerOpen(false)}
            confirm={(asset) => {
              if (asset?.url) {
                handleChange({ target: { name: 'bubbleIcon', value: asset.url } });
              }
              setIconPickerOpen(false);
            }}
          />
          <Text size="xs" c="dimmed">
            Image that replaces the default Releva icon across the chat widget
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Widget Position</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            w="100%"
            allowDeselect={false}
            data={[
              { value: 'right', label: 'Right' },
              { value: 'left', label: 'Left' },
            ]}
            value={chatBot.position}
            onChange={(value) => handleChange({ target: { name: 'position', value } })}
          />
          <Text size="xs" c="dimmed">
            Position the chatbot widget on the left or right side of the screen
          </Text>
        </Grid.Col>
      </Grid>

      {/* Super admin only, next to the position it layers over. */}
      <SuperAdminArea show={isSuperAdmin}>
        <SettingRow label="Custom z-index" htmlFor="zIndexEnabled" help={<HelpPopover {...HELP.zIndexEnabled} />}>
          <Switch
            id="zIndexEnabled"
            size="xs"
            checked={chatBot.zIndex !== null}
            onChange={(e) => handleChange({ target: { name: 'zIndexEnabled', value: e.currentTarget.checked } })}
          />
        </SettingRow>
        {chatBot.zIndex !== null && (
          <SettingRow
            label="Z-index base"
            htmlFor="zIndex"
            help={<HelpPopover {...HELP.zIndex} onUse={applyExample(handleChange, 'zIndex', chatBot.zIndex)} />}
          >
            <NumberInput
              id="zIndex"
              name="zIndex"
              size="xs"
              w={160}
              value={chatBot.zIndex || 99999990}
              min={1}
              max={2147483647}
              onChange={(value) => handleChange({ target: { name: 'zIndex', value: value === '' ? 99999990 : value } })}
            />
          </SettingRow>
        )}
      </SuperAdminArea>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Product Results Display</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            w="100%"
            allowDeselect={false}
            data={[
              { value: 'list', label: 'List' },
              { value: 'carousel', label: 'Carousel' },
            ]}
            value={chatBot.productDisplayMode || 'list'}
            onChange={(value) => handleChange({ target: { name: 'productDisplayMode', value } })}
          />
          <Text size="xs" c="dimmed">
            Show product previews in chat replies as a stacked list or a horizontal carousel
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Notification Bar</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Switch
            checked={chatBot.notificationEnabled}
            onChange={(e) => {
              handleChange({ target: { name: 'notificationEnabled', value: e.currentTarget.checked } });
            }}
          />
          <Text size="xs" c="dimmed">
            Display a proactive notification bar with quick actions to engage users before they open the chat
          </Text>
        </Grid.Col>
      </Grid>

      {chatBot.notificationEnabled && (
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Notification Delay (seconds)</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <NumberInput
              name="notificationDelay"
              value={chatBot.notificationDelay || 5}
              min={1}
              max={60}
              onChange={(value) =>
                handleChange({ target: { name: 'notificationDelay', value: value === '' ? 5 : value } })
              }
            />
            <Text size="xs" c="dimmed">
              Time in seconds before showing the notification bar after page load
            </Text>
          </Grid.Col>
        </Grid>
      )}

      {chatBot.notificationEnabled && (
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Notification Send Button</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Switch
              checked={!!appearance.sendWhenText}
              onChange={(e) => setAppearance({ sendWhenText: e.currentTarget.checked })}
            />
            <Text size="xs" c="dimmed">
              Reveal the send button in the notification input only after the visitor types; show a dismiss (×) instead
              while it is empty
            </Text>
          </Grid.Col>
        </Grid>
      )}

      <WidgetColorsSection
        chatBot={chatBot}
        handleChange={handleChange}
        setAppearance={setAppearance}
        brandContext={brandContext}
        brandHref={brandHref}
      />

      <Divider my="md" label="Launcher (line / bubble)" labelPosition="left" />

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Launcher on Desktop</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            w="100%"
            allowDeselect={false}
            data={LAUNCHER_FORMAT_OPTIONS}
            value={appearance.desktop || 'auto'}
            onChange={(value) => setAppearance({ desktop: value })}
          />
          <Text size="xs" c="dimmed">
            Shape of the launcher on desktop. Auto shows the line bar and morphs to the bubble on scroll.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Launcher on Mobile</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            w="100%"
            allowDeselect={false}
            data={LAUNCHER_FORMAT_OPTIONS}
            value={appearance.mobile || 'auto'}
            onChange={(value) => setAppearance({ mobile: value })}
          />
          <Text size="xs" c="dimmed">
            Shape on mobile. Pick "Bubble" to show only the round bubble on phones.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Collapse Control</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Switch
            checked={!!appearance.collapse}
            onChange={(e) => setAppearance({ collapse: e.currentTarget.checked })}
          />
          <Text size="xs" c="dimmed">
            Show a × on the line that collapses it into the bubble for the rest of the page (hover to reveal on desktop,
            always shown on mobile). Does not hide the widget.
          </Text>
        </Grid.Col>
      </Grid>

      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
          <label>Launcher Offset (px)</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Group grow>
            <NumberInput
              label="Horizontal (X)"
              value={appearance.offsetX ?? 24}
              min={0}
              max={200}
              onChange={(value) => setAppearance({ offsetX: value === '' ? 0 : value })}
            />
            <NumberInput
              label="Vertical (Y)"
              value={appearance.offsetY ?? 24}
              min={0}
              max={200}
              onChange={(value) => setAppearance({ offsetY: value === '' ? 0 : value })}
            />
          </Group>
          <Text size="xs" c="dimmed">
            Distance from the screen corner, applied on desktop and mobile
          </Text>
        </Grid.Col>
      </Grid>

      <URLFilterConfig
        labelCol={{ base: 12, sm: 4 }}
        selectorCol={{ base: 12, sm: 8 }}
        modelInstance={chatBot}
        modelName="sales agent"
        handleChange={handleChange}
      />
    </Card>
  );
};

export default CustomizeTab;
